import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Brief, CadenceConfig } from "../../../shared/types";

interface CadenceResponse {
  data: {
    config: CadenceConfig | null;
    upcoming: Brief[];
  };
}

export function useCadence(brandId: string | undefined) {
  return useQuery({
    queryKey: ["cadence", brandId],
    queryFn: () => api.get<CadenceResponse>(`/api/brands/${encodeURIComponent(brandId!)}/cadence`),
    enabled: !!brandId,
  });
}

export function useCreateAdhocCadenceSlot(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { surface: Brief["surface"]; scheduled_at: string; campaign_id: string }) =>
      api.post<{ data: Brief }>(`/api/brands/${encodeURIComponent(brandId!)}/cadence/slot`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cadence", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      qc.invalidateQueries({ queryKey: ["task-runs", brandId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
    },
  });
}

export function useRunBrandTask(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { task_slug: string; params?: Record<string, unknown> }) =>
      api.post<{ run_id: string }>(`/api/brands/${encodeURIComponent(brandId!)}/tasks`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-runs", brandId] });
      qc.invalidateQueries({ queryKey: ["task-catalog", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      qc.invalidateQueries({ queryKey: ["cadence", brandId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
    },
  });
}
